import React, { useState, useEffect, useRef } from 'react';
import {Menu, X, ChevronDown, Heart, MapPin, Compass, BookOpen} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { LanguageSwitcher } from './LanguageSwitcher';
import { translations } from '../translations';
import {
  footerServiceLinks,
  footerLocationLinks,
  footerGuideLinks,
  footerRatgeberLinks,
} from '../data/seoPages';

interface HeaderProps {
  currentLang: 'de' | 'en' | 'es';
  onLanguageChange: (lang: 'de' | 'en' | 'es') => void;
}

const megaMenuLabels = {
  de: {
    trigger: 'Angebote & Info',
    service: 'Pflege & Gesundheit',
    location: 'Standorte auf Mallorca',
    guide: 'Guide – Mallorca erleben',
    ratgeber: 'Medizinischer Ratgeber',
    brandLine: 'Geis Care Mallorca – Diplomierte Pflege & Begleitung',
    cta: 'Jetzt Erstgespräch anfragen',
  },
  en: {
    trigger: 'Offers & Info',
    service: 'Care & Health',
    location: 'Locations on Mallorca',
    guide: 'Guide – Discover Mallorca',
    ratgeber: 'Medical Guide',
    brandLine: 'Geis Care Mallorca – Qualified nursing & support',
    cta: 'Request an initial consultation',
  },
  es: {
    trigger: 'Servicios & Info',
    service: 'Cuidado & Salud',
    location: 'Ubicaciones en Mallorca',
    guide: 'Guía – Descubrir Mallorca',
    ratgeber: 'Guía médica',
    brandLine: 'Geis Care Mallorca – Enfermería titulada y acompañamiento',
    cta: 'Solicitar una primera consulta',
  },
};

export const Header: React.FC<HeaderProps> = ({ currentLang, onLanguageChange }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMegaMenuOpen, setIsMegaMenuOpen] = useState(false);
  const [mobileExpandedSection, setMobileExpandedSection] = useState<string | null>(null);
  const megaMenuRef = useRef<HTMLDivElement>(null);
  const t = translations[currentLang].header;
  const ml = megaMenuLabels[currentLang];
  const navigate = useNavigate();
  const isInternalPage = window.location.pathname !== '/';
  const useSolidHeader = isScrolled || isInternalPage;

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (megaMenuRef.current && !megaMenuRef.current.contains(e.target as Node)) {
        setIsMegaMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  const handleNav = (path: string) => {
    navigate(path);
    setIsMegaMenuOpen(false);
    setIsMobileMenuOpen(false);
  };

  const navTextClass = `text-sm hover:text-[#A68B5B] transition-colors font-medium ${
    useSolidHeader ? 'text-[#333333]' : 'text-white drop-shadow-md'
  }`;

  const sections = [
    {
      key: 'service',
      icon: <Heart className="w-4 h-4 text-[#A68B5B]" />,
      label: ml.service,
      links: footerServiceLinks,
      buildPath: (slug: string) => `/${slug}`,
    },
    {
      key: 'location',
      icon: <MapPin className="w-4 h-4 text-[#A68B5B]" />,
      label: ml.location,
      links: footerLocationLinks,
      buildPath: (slug: string) => `/${slug}`,
    },
    {
      key: 'guide',
      icon: <Compass className="w-4 h-4 text-[#A68B5B]" />,
      label: ml.guide,
      links: footerGuideLinks,
      buildPath: (slug: string) => `/guide/${slug}`,
    },
    {
      key: 'ratgeber',
      icon: <BookOpen className="w-4 h-4 text-[#A68B5B]" />,
      label: ml.ratgeber,
      links: footerRatgeberLinks,
      buildPath: (slug: string) => `/ratgeber/${slug}`,
    },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        useSolidHeader ? 'bg-white/95 backdrop-blur-sm shadow-sm py-3' : 'bg-transparent py-4'
      }`}
      role="banner"
      itemScope
      itemType="https://schema.org/WPHeader"
    >
      <div className="container mx-auto px-6 flex justify-between items-center">
        {/* Logo */}
        <div className="flex items-center gap-3 cursor-pointer" onClick={() => scrollToSection('hero')}>
          <img
            src="https://static.lumi.new/62/6273e47ef7f9c27d594019243bc561a8.png"
            alt="Geis Care"
            className="h-12 transition-all duration-300"
          />
        </div>

        {/* Desktop Nav */}
        <nav
          className={`${isInternalPage ? 'hidden lg:flex' : 'hidden md:flex'} items-center gap-5`}
          role="navigation"
          aria-label="Hauptnavigation"
        >
          <button onClick={() => scrollToSection('about')} className={navTextClass}>{t.about}</button>
          <button onClick={() => scrollToSection('usp')} className={navTextClass}>{t.why}</button>
          <button onClick={() => scrollToSection('services')} className={navTextClass}>{t.services}</button>

          {/* Mega Menu Trigger */}
          <div className="relative" ref={megaMenuRef} style={{ position: 'static' }}>
            <button
              onClick={() => setIsMegaMenuOpen(!isMegaMenuOpen)}
              className={`flex items-center gap-1 text-sm font-medium transition-colors hover:text-[#A68B5B] ${
                useSolidHeader ? 'text-[#333333]' : 'text-white drop-shadow-md'
              }`}
            >
              {ml.trigger}
              <ChevronDown
                className={`w-4 h-4 transition-transform duration-200 ${isMegaMenuOpen ? 'rotate-180' : ''}`}
              />
            </button>

            {/* Mega Dropdown */}
            {isMegaMenuOpen && (
              <div
                className="fixed mt-3 w-[860px] max-w-[calc(100vw-32px)] bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden"
                style={{
                  top: useSolidHeader ? '64px' : '72px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                }}
              >
                {/* Top accent bar */}
                <div className="h-1 bg-gradient-to-r from-[#A68B5B] via-[#d4b483] to-[#A68B5B]" />

                <div className="grid grid-cols-4 gap-0 p-6">
                  {sections.map((section, idx) => (
                    <div
                      key={section.key}
                      className={`pr-5 ${idx < sections.length - 1 ? 'border-r border-gray-100 mr-5' : ''}`}
                    >
                      {/* Section Header */}
                      <div className="flex items-center gap-2 mb-3 pb-2 border-b border-[#A68B5B]/20">
                        {section.icon}
                        <span className="text-xs font-semibold text-[#A68B5B] uppercase tracking-wider">
                          {section.label}
                        </span>
                      </div>
                      {/* Links */}
                      <ul className="space-y-1">
                        {section.links.map((link) => (
                          <li key={link.slug}>
                            <button
                              onClick={() => handleNav(section.buildPath(link.slug))}
                              className="w-full text-left text-sm text-[#555] hover:text-[#A68B5B] hover:bg-[#EFE8DC]/50 rounded-lg px-2 py-1.5 transition-all duration-150 leading-snug"
                            >
                              {link.labels[currentLang]}
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>

                {/* Bottom CTA */}
                <div className="bg-[#EFE8DC]/40 px-6 py-3 flex items-center justify-between border-t border-gray-100">
                  <span className="text-xs text-[#888]">{ml.brandLine}</span>
                  <button
                    onClick={() => { scrollToSection('contact'); setIsMegaMenuOpen(false); }}
                    className="text-xs font-semibold text-[#A68B5B] hover:underline"
                  >
                    {ml.cta} →
                  </button>
                </div>
              </div>
            )}
          </div>

          <button onClick={() => scrollToSection('blog')} className={navTextClass}>{t.blog}</button>
          <a
            href="https://shop.geiscare.com"
            target="_blank"
            rel="noopener noreferrer"
            className={`flex items-center gap-2 text-sm hover:text-[#A68B5B] transition-colors font-medium ${
              useSolidHeader ? 'text-[#333333]' : 'text-white drop-shadow-md'
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
            </svg>
            {t.shop}
          </a>
          <LanguageSwitcher currentLang={currentLang} onLanguageChange={onLanguageChange} />
          <button
            onClick={() => scrollToSection('contact')}
            className="bg-care text-secondary px-6 py-3 rounded-full hover:bg-care/90 transition-all hover:shadow-lg text-sm font-semibold transform hover:scale-105"
          >
            {t.contact}
          </button>
        </nav>

        {/* Mobile Menu Button */}
        <div className={`${isInternalPage ? 'lg:hidden' : 'md:hidden'} flex items-center gap-3`}>
          <LanguageSwitcher currentLang={currentLang} onLanguageChange={onLanguageChange} />
          <button
            className={`transition-colors ${useSolidHeader ? 'text-[#333333]' : 'text-white drop-shadow-md'}`}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      {/* Mobile Nav */}
      {isMobileMenuOpen && (
        <div className={`${isInternalPage ? 'lg:hidden' : 'md:hidden'} absolute top-full left-0 right-0 bg-white border-t border-gray-100 py-4 px-6 shadow-lg max-h-[80vh] overflow-y-auto`}>
          <div className="flex flex-col space-y-2">
            <button onClick={() => scrollToSection('about')} className="text-left text-[#333333] py-2 hover:text-[#A68B5B] transition-colors font-medium">{t.about}</button>
            <button onClick={() => scrollToSection('usp')} className="text-left text-[#333333] py-2 hover:text-[#A68B5B] transition-colors font-medium">{t.why}</button>
            <button onClick={() => scrollToSection('services')} className="text-left text-[#333333] py-2 hover:text-[#A68B5B] transition-colors font-medium">{t.services}</button>

            {/* Mobile Mega Sections */}
            {sections.map((section) => (
              <div key={section.key} className="border-t border-gray-100 pt-2">
                <button
                  className="w-full flex items-center justify-between py-2 font-semibold text-[#333333] hover:text-[#A68B5B] transition-colors"
                  onClick={() => setMobileExpandedSection(
                    mobileExpandedSection === section.key ? null : section.key
                  )}
                >
                  <span className="flex items-center gap-2">
                    {section.icon}
                    {section.label}
                  </span>
                  <ChevronDown
                    className={`w-4 h-4 transition-transform duration-200 ${
                      mobileExpandedSection === section.key ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                {mobileExpandedSection === section.key && (
                  <div className="pl-6 pb-2 grid grid-cols-2 gap-1">
                    {section.links.map((link) => (
                      <button
                        key={link.slug}
                        onClick={() => handleNav(section.buildPath(link.slug))}
                        className="text-left text-sm text-[#555] hover:text-[#A68B5B] py-1.5 transition-colors leading-snug"
                      >
                        {link.labels[currentLang]}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}

            <div className="border-t border-gray-100 pt-2">
              <button onClick={() => scrollToSection('blog')} className="text-left text-[#333333] py-2 hover:text-[#A68B5B] transition-colors font-medium w-full">{t.blog}</button>
              <a href="https://shop.geiscare.com" target="_blank" rel="noopener noreferrer" className="block text-left text-[#333333] py-2 hover:text-[#A68B5B] transition-colors font-medium">{t.shop}</a>
              <button
                onClick={() => scrollToSection('contact')}
                className="w-full mt-2 bg-care text-secondary px-6 py-3 rounded-full text-center hover:bg-care/90 transition-colors font-semibold"
              >
                {t.contact}
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};
